import { FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  previewPromotionCode,
  PromotionCodePreview,
} from '../../../../services/subscriptions';
import { capture } from '../../../../utils/analytics';
import { getEligiblePromotionPlanLabels } from '../../../../pages/Pricing/promotionCode';

interface PromoCodeInputProps {
  hasAutomaticDiscount: boolean;
  appliedPreview: PromotionCodePreview | null;
  onApplied: (preview: PromotionCodePreview) => void;
  onCleared: () => void;
}

const formatPlanList = (plans: string[]): string => {
  if (plans.length <= 1) return plans[0] ?? '';
  if (plans.length === 2) return plans.join(' and ');
  return `${plans.slice(0, -1).join(', ')}, and ${plans[plans.length - 1]}`;
};

const PromoCodeInput = ({
  hasAutomaticDiscount,
  appliedPreview,
  onApplied,
  onCleared,
}: PromoCodeInputProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [promotionCode, setPromotionCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedCode = promotionCode.trim();
    if (!trimmedCode || isApplying) return;

    setIsApplying(true);
    setErrorMessage(null);
    capture('pricing_promo_code_apply_attempted');

    try {
      const preview = await previewPromotionCode(trimmedCode);
      const eligiblePlans = getEligiblePromotionPlanLabels(preview);

      if (eligiblePlans.length === 0) {
        throw new Error(
          "That promo code isn't valid for any of the available plans."
        );
      }

      onApplied(preview);
      setPromotionCode('');

      capture('pricing_promo_code_apply_succeeded', {
        eligiblePlans,
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'We could not validate that promo code. Please try again.';

      setErrorMessage(message);
      capture('pricing_promo_code_apply_failed');
    } finally {
      setIsApplying(false);
    }
  };

  if (appliedPreview) {
    const eligiblePlans = getEligiblePromotionPlanLabels(appliedPreview);
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center gap-2 text-center">
        <p className="m-0 text-sm text-fg-secondary" role="status">
          Promo code applied to {formatPlanList(eligiblePlans)}.
        </p>
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0 text-sm"
          onClick={() => {
            onCleared();
            setIsExpanded(false);
            setErrorMessage(null);
            capture('pricing_promo_code_cleared');
          }}
        >
          {hasAutomaticDiscount
            ? 'Use automatic discount instead'
            : 'Remove promo code'}
        </Button>
      </div>
    );
  }

  if (!isExpanded) {
    return (
      <div className="text-center">
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0 text-sm"
          onClick={() => {
            setIsExpanded(true);
            setErrorMessage(null);
          }}
          aria-expanded="false"
        >
          {hasAutomaticDiscount
            ? 'Have a different promo code?'
            : 'Have a promo code?'}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="mx-auto flex max-w-xl flex-col gap-2"
      onSubmit={(event) => void handleSubmit(event)}
    >
      <label
        className="text-sm font-medium text-fg-primary"
        htmlFor="promo-code"
      >
        Promo code
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="promo-code"
          name="promotionCode"
          inputSize="sm"
          value={promotionCode}
          onChange={(event) => {
            setPromotionCode(event.target.value);
            if (errorMessage) setErrorMessage(null);
          }}
          placeholder="Enter promo code"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          disabled={isApplying}
          state={errorMessage ? 'error' : 'default'}
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? 'promo-code-error' : undefined}
          autoFocus
        />
        <Button
          type="submit"
          size="sm"
          className="sm:min-w-24"
          disabled={!promotionCode.trim() || isApplying}
        >
          {isApplying ? 'Applying…' : 'Apply'}
        </Button>
      </div>
      {errorMessage ? (
        <p
          id="promo-code-error"
          className="m-0 text-sm text-status-danger"
          role="alert"
        >
          {errorMessage}
        </p>
      ) : (
        <p className="m-0 text-xs text-fg-secondary">
          A valid promo code replaces the automatic discount on eligible plans.
        </p>
      )}
    </form>
  );
};

export default PromoCodeInput;
